// src/components/personal/sections/ClientsSection/components/ClientCard/ClientChart.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS } from '@/theme';
import { BarChart } from '@/components/ui/Charts';
import { TEXT_STYLES } from '@/theme/typography';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

interface OrderByMonthType {
  month: string;
  orders: number;
  total: number;
}

interface ClientChartProps {
  ordersByMonth: OrderByMonthType[];
}

// Определяем стили как константу
const chartStyles = `
  /* Обеспечиваем полную ширину для компонентов Recharts */
  .client-chart .recharts-wrapper,
  .client-chart .recharts-responsive-container {
    width: 100% !important;
    height: 100% !important;
  }
  
  /* Уменьшаем размер текста осей */
  .client-chart .recharts-cartesian-axis-tick-value {
    font-size: 11px;
  }
  
  /* Корректируем положение оси X */
  .client-chart .recharts-xAxis .recharts-cartesian-axis-tick {
    transform: translateY(-5px);
  }
  
  /* Увеличиваем высоту графика */
  .client-chart .recharts-surface {
    height: 100% !important;
  }
  
  /* Убираем внутренние отступы */
  .modal-body {
    padding: 0 !important;
    margin-top: 0 !important;
  }
  
  /* Уменьшаем отступы между элементами */
  .space-y-6 > * + * {
    margin-top: 0.75rem !important;
  }
`;

const ClientChart: React.FC<ClientChartProps> = ({ ordersByMonth }) => {
  const { t } = useTranslation(['clients']);
  const { currencySymbol } = useBusinessCurrency();
  
  // Настройка серии данных для графика
  const series = [
    {
      name: t('chart.orders_count'),
      dataKey: 'orders',
      color: COLORS.secondary
    },
    {
      name: `${t('chart.orders_total')} (${currencySymbol})`, 
      dataKey: 'total',
      color: COLORS.primary
    }
  ];

  const hasData = ordersByMonth && 
                 Array.isArray(ordersByMonth) && 
                 ordersByMonth.length > 0 && 
                 ordersByMonth.some(item => item.orders > 0 || item.total > 0);

  return (
    <div className="client-chart w-full" style={{ margin: 0, padding: 0 }}>
      {/* Внедряем заголовок и подзаголовок напрямую для экономии места */}
      <div className="mb-2">
        <h3 className={`${TEXT_STYLES.subtitle} text-text`}>{t('chart.title')}</h3>
        <p className={`${TEXT_STYLES.small} text-secondary`}>{t('chart.subtitle')}</p>
      </div>
      
      {/* Контейнер для графика без использования ChartCard */}
      <div 
        className="chart-container bg-white rounded-lg border border-gray-200 overflow-hidden"
        style={{ 
          width: '100%', 
          height: '350px', 
          padding: '8px'
        }}
      >
        {hasData ? (
          <div style={{ width: '100%', height: '100%' }}>
            <BarChart 
              data={ordersByMonth} 
              series={series}
              xAxisKey="month"
              showGrid={true}
              borderRadius={4}
              barSize={45} // Увеличиваем размер столбцов
              // Минимизируем отступы
              margin={{ 
                top: 5, 
                right: 5, 
                bottom: 25, 
                left: 5 
              }}
              size="full"
              // Убираем легенду для экономии места
              showLegend={false}
            />
          </div>
        ) : (
          <div className="flex justify-center items-center h-full">
            <p className={`${TEXT_STYLES.body} text-secondary`}>
              {t('chart.no_data')}
            </p>
          </div>
        )}
      </div>
      
      {/* Добавляем легенду отдельно под графиком для экономии места */}
      {hasData && (
        <div className="flex justify-center items-center mt-2 gap-4">
          {series.map((serie, index) => (
            <div key={index} className="flex items-center">
              <div 
                className="w-4 h-4 mr-1 rounded" 
                style={{ backgroundColor: serie.color }} 
              />
              <span className={`${TEXT_STYLES.small} text-text`}>{serie.name}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Стандартный React способ подключения глобальных стилей */}
      <style>{chartStyles}</style>
    </div>
  );
};

export default ClientChart;